'use client'

import type { Row } from '@tanstack/react-table'

import { InterfaceChainUtils, parseApiAsset } from '@morpho-org/dapp-core'
import { Card, Skeleton } from '@morpho-org/ui-v6/server'
import { Separator } from '@repo/ui'

import { MobileVaultCard } from '@/components/common/MobileEarnCards'
import { getVaultUrl } from '@/helpers/routing'

import type { TableVaultFeatures } from '../config/TableVault.columns'
import type { TableVaultRow } from '../config/TableVault.types'

interface MobileVaultCardListProps {
  rows: Row<TableVaultFeatures, TableVaultRow>[]
  isLoading: boolean
  isFetchingMore: boolean
  skeletonCount: number
}

function MobileVaultCardItem({ row }: { row: Row<TableVaultFeatures, TableVaultRow> }) {
  // 'use no memo': TanStack keeps this object's reference while table state (sorting, rows) changes.
  'use no memo'

  const { vault, deposits, liquidity, curators, exposure, rewards } = row.original

  const href = getVaultUrl({
    identifier: InterfaceChainUtils.getIdentifierById(vault.chainId),
    vaultAddress: vault.address,
    vaultName: vault.name
  })

  return (
    <MobileVaultCard
      href={href}
      vault={{
        address: vault.address,
        name: vault.name,
        asset: parseApiAsset(vault.asset),
        chainId: vault.chainId,
        isVaultV2: vault.version === '2.0',
        hasUserPosition: vault.hasUserPosition,
        warnings: vault.warnings
      }}
      apy={{
        native: row.original.apy,
        net: row.original.netApy,
        rewards,
        performanceFee: row.original.performanceFeeBigInt,
        managementFee: row.original.managementFeeBigInt,
        maxApy: row.original.maxApy,
        isRateCapped: row.original.isRateCapped,
        includesDonations: row.original.includesDonations,
        apyBreakdown: row.original.apyBreakdown,
        points: []
      }}
      totalDeposits={{
        amount: deposits.amount,
        usdValue: deposits.valueUsd
      }}
      liquidity={{
        amount: liquidity.amount,
        usdValue: liquidity.valueUsd
      }}
      curators={curators}
      collateralExposures={exposure.collateralExposures}
    />
  )
}

function MobileVaultCardSkeleton() {
  return (
    <Card data-testid="listing-card" padding="s" withBorder className="gap-sm flex flex-col">
      <div className="gap-sm flex flex-col">
        <div className="flex items-center justify-between">
          <div className="gap-xs flex items-center">
            <Skeleton variant="circle" className="size-[24px]" />
            <Skeleton className="h-[16px] w-[120px]" />
          </div>
          <Skeleton className="h-md w-[50px]" />
        </div>

        <Separator className="my-xs" />

        <div className="h-md flex items-center justify-between">
          <Skeleton className="h-[16px] w-[55px]" />
          <div className="gap-2xs flex items-center">
            <Skeleton variant="circle" className="size-[16px]" />
            <Skeleton className="h-[14px] w-[50px]" />
          </div>
        </div>
        <div className="gap-sm flex items-center justify-between">
          <Skeleton className="h-[16px] w-20" />
          <Skeleton className="h-md w-[100px]" />
        </div>
        <div className="flex items-center justify-between">
          <Skeleton className="h-[16px] w-[60px]" />
          <Skeleton className="h-md w-[90px]" />
        </div>
        <div className="flex items-center justify-between">
          <Skeleton className="h-[16px] w-[50px]" />
          <Skeleton className="h-md w-20" />
        </div>
        <div className="flex items-center justify-between">
          <Skeleton className="h-[16px] w-[60px]" />
          <Skeleton className="h-md w-[40px]" />
        </div>

        <Separator className="my-xs" />

        <Skeleton className="h-lg w-full" />
      </div>
    </Card>
  )
}

function MobileVaultCardList({
  rows,
  isLoading,
  isFetchingMore,
  skeletonCount
}: MobileVaultCardListProps) {
  // 'use no memo': TanStack keeps this object's reference while table state (sorting, rows) changes.
  'use no memo'

  return (
    <div className="gap-sm flex flex-col">
      {isLoading
        ? Array.from({ length: skeletonCount }).map((_, index) => (
            <MobileVaultCardSkeleton key={`skeleton-mobile-${index}`} />
          ))
        : rows.map(row => <MobileVaultCardItem key={row.id} row={row} />)}
      {isFetchingMore &&
        Array.from({ length: skeletonCount }).map((_, index) => (
          <MobileVaultCardSkeleton key={`skeleton-more-${index}`} />
        ))}
    </div>
  )
}

export default MobileVaultCardList
